import FileSaver from "file-saver";
import { isSpecialBDCountryCodes, isSpecialCountryCode } from "./CountryHelper";
import { MAX_SEARCH_TERMS } from "../constants/offlineApp-constants";
import { formatAbbreviatedNumber, formatNumberToBillions, isVnCountryCode } from "../utils/numberFormat";
import { EXCEL_FORMAT_ERR, EXCEL_UPLOAD_MAX_ERR_MSG, FILE_FORMAT, FILE_SIZE } from "../components/OutletGeoCompliance/OGComplianceBulkUpload/BulkUpdateModal.constant";

export const getFileNameWithTimestamp = (fileName) => {
  const fileExtension = fileName
    .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
    .toLowerCase();
  return `${fileName
    .split(".")
    .slice(0, -1)
    .join(".")}_${Date.now()}.${fileExtension}`;
};

export const filterNonBlankRows = (rows) => {
  const filteredRows = rows.filter((row) => {
    const columns = row.split(",").map((col) => col.trim());
    return columns.some((col) => col !== "");
  });
  return filteredRows.join("\n");
};

export const getNonBlankRowsFile = (rows, file) => {
  const filteredContent = filterNonBlankRows(rows);
  return new Blob([filteredContent], { type: file.type });
};

export const downloadTemplate = (fileBase64, type = "csv") => {
  let byteCharacters = atob(fileBase64);
  let bytesLength = byteCharacters.length;
  let size = 1024;
  let slicesCount = Math.ceil(bytesLength / size);
  let byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    let start = sliceIndex * size;
    let end = Math.min(start + size, bytesLength);
    let bytes = new Array(end - start);
    for (let offset = start, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }

  const normalizedType = String(type || "").toLowerCase();
  const isCsvTemplate = normalizedType === "csv";

  if (isCsvTemplate) {
    const firstChunk = byteArrays[0] || new Uint8Array();
    const hasUtf8Bom =
      firstChunk.length >= 3 &&
      firstChunk[0] === 0xef &&
      firstChunk[1] === 0xbb &&
      firstChunk[2] === 0xbf;

    const csvBlob = hasUtf8Bom
      ? new Blob(byteArrays, { type: "text/csv;charset=UTF-8" })
      : new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), ...byteArrays], {
          type: "text/csv;charset=UTF-8"
        });

    FileSaver.saveAs(csvBlob, `Template.${type}`);
    return;
  }

  FileSaver.saveAs(new Blob(byteArrays), `Template.${type}`);
};

export const redirectIfInvalidConfig = ({
  configData,
  selectedCountryCode,
  offlineApp,
  history,
}) => {
  const isOfflineAppEnabled =
    configData?.common[selectedCountryCode]?.OfflineApp;
  if (!isOfflineAppEnabled || !offlineApp) {
    history.push("/adminconfigurationpage");
  }
};

export const isSalesRole = (currentRole) => {
  return currentRole === "Salesman" || currentRole === "Sales Officer" || currentRole === "SM";
};

export const shouldRenderOfflineApp = (
  configData,
  selectedCountryCode,
  offlineApp
) => {
  return configData?.common[selectedCountryCode]?.OfflineApp && offlineApp;
};

export const getHeaderNameWithCurrency = (column, selectedCountryCode) => {
  if (!column || !column.headerName || !selectedCountryCode) {
    return column ? column.headerName : "";
  }

  const currencySymbols = {
    vn: "₫",
    ph: "₱",
    th: "฿",
    bd: "TK",
    kh: "$",
    la: "LAK",
    my: "RM"
  };

  const currencySuffix = currencySymbols[selectedCountryCode] || "";

  const specialHeaders = [
    "Invoice SKU Price",
    "Invoice SKU Amount",
    "Invoice SKU Return Price",
    "Invoice SKU Return Amount",
  ];

  if (specialHeaders.includes(column.headerName)) {
    return `${column.headerName} (${currencySuffix})`;
  }

  return column.headerName;
};

export const getRoleBasedOnCountryCode = (countryCode) => {
  if (!countryCode) return "Branch Managers";

  if (isSpecialCountryCode(countryCode)) {
    return "RSD";
  }

  if (isSpecialBDCountryCodes(countryCode)) {
    return "AM";
  }

  return "Branch Managers";
};

export const getConditionEqualValue = (
  value1,
  value2,
  trueValue,
  falseValue
) => {
  return value1 === value2 ? trueValue : falseValue;
};

export const getConditionUnEqualValue = (
  value1,
  value2,
  trueValue,
  falseValue
) => {
  return value1 !== value2 ? trueValue : falseValue;
};

export const getConditionValue = (value, trueValue, falseValue) => {
  return value ? trueValue : falseValue;
};

export const getSearchOptions = (searchOptions, getOptions) => {
  return searchOptions?.length >= MAX_SEARCH_TERMS ? [] : getOptions();
};

export const getStartDateBySelection = (
  selectedValue,
  todaysDate,
  startDate,
  startDatePrev
) => {
  if (selectedValue === "day") {
    return todaysDate;
  } else if (selectedValue === "mtd") {
    return startDate;
  } else {
    return startDatePrev;
  }
};

export const getEndDateBySelection = (
  selectedValue,
  todaysDate,
  lastDate,
  lastDatePrev
) => {
  if (selectedValue === "day") {
    return todaysDate;
  } else if (selectedValue === "mtd") {
    return lastDate;
  } else {
    return lastDatePrev;
  }
};

export const getRoleLabel = (level, role, location, isDistributor) => {
  if (level && level !== role && location) {
    return isDistributor ? "Main Distributor" : "Main Team";
  }
  return "";
};

export const getNumberValue = (value, countryCode) => {
  const showValue = ((!value || value === "NA") && value !== 0) ? "0.00" : value
  const showDecimal = showValue?.toString().includes(".")

  if (value > 1000) {
    return formatAbbreviatedNumber(value, countryCode);
  }
  if (isVnCountryCode(countryCode)) {
    return formatNumberToBillions(showValue);
  }
  return showDecimal ? parseFloat(showValue).toFixed(2) : parseInt(showValue);
};

export const getPathValues = (pathArray) => {
  let array = pathArray?.sort(
      (a, b) =>
        parseInt(a.geoLocationSequence) - parseInt(b.geoLocationSequence)
    );;
  let RoundOffValue = 7;

  const createPoints = (points, roundoff) => {
    let croppedWaypoint = points.map((item) => {
      return {
        ...item,
        salesmanLatitudes: parseFloat(item.salesmanLatitudes).toFixed(
          roundoff
        ),
        salesmanLongitudes: parseFloat(item.salesmanLongitudes).toFixed(
          roundoff
        ),
      };
    });

    croppedWaypoint = [
      ...croppedWaypoint
        .reduce((map, { salesmanLatitudes, salesmanLongitudes }) => {
          return map.set(`${salesmanLatitudes}-${salesmanLongitudes}`, {
            salesmanLatitudes,
            salesmanLongitudes,
          });
        }, new Map())
        .values(),
    ];

    if(croppedWaypoint.length > 25){
      const newRound = roundoff - 1
      return createPoints(croppedWaypoint, newRound)
    }
    else{
      return croppedWaypoint
    }
  };

  if (array.length <= 25) {
    return array;
  } else {
    const newPoints = createPoints(array, RoundOffValue)
    return newPoints
  }
};

export const validateFileMeta = (file, setFileError) => {
  const ext = file.name.split(".").pop();
  if (ext !== "csv") {
    setFileError({ header: FILE_FORMAT, message: EXCEL_FORMAT_ERR });
    return false;
  }

  if (!/^[A-Za-z0-9 _-]+\.csv$/.test(file.name)) {
    setFileError({
      header: FILE_FORMAT,
      message:
        "File name can only contain letters, numbers, spaces, hyphens, and underscores, followed by .csv.",
    });
    return false;
  }

  const fileSize = file.size / 1024 / 1024;
  if (!fileSize || fileSize > 25) {
    setFileError({ header: FILE_SIZE, message: EXCEL_UPLOAD_MAX_ERR_MSG });
    return false;
  }

  return true;
};

export const validateField = ({
  row,
  field,
  setFileError,
  resolve,
  validateFn,
  errorHeader,
  errorMessageFn
}) => {
  const value = row[field];

  if (!validateFn(value)) {
    setFileError({
      header: errorHeader,
      message: errorMessageFn(row),
    });
    resolve("invalid");
    return true;
  }

  return false;
};

export const findFirstDuplicateRow = (rows, fields) => {
  if (!Array.isArray(rows) || !fields?.length) return null;

  const firstIndexByKey = new Map();
  let earliestDuplicateIndex = -1;

  rows.forEach((row, rowIndex) => {
    const key = JSON.stringify(
      fields.map((field) => [typeof row[field], row[field]])
    );

    if (firstIndexByKey.has(key)) {
      const firstRowIndex = firstIndexByKey.get(key);
      if (earliestDuplicateIndex === -1 || firstRowIndex < earliestDuplicateIndex) {
        earliestDuplicateIndex = firstRowIndex;
      }
    } else {
      firstIndexByKey.set(key, rowIndex);
    }
  });

  return earliestDuplicateIndex === -1 ? null : rows[earliestDuplicateIndex];
};

//Safely splits a CSV row string into an array, handling quoted fields and escaped quotes.
export const splitCSVRow = (row) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  let i = 0;
  while (i < row.length) {
    const char = row[i];
    const nextChar = row[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }

    i++;
  }

  result.push(current);
  return result;
};

export const formatCsvFile = (file, targetColumns = []) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      const rawContent = e.target.result;
      const lines = rawContent.split(/\r?\n/);
      if (!lines.length) return reject(new Error("Empty CSV file"));

       // === Remove only fully empty rows ===
      const cleanedLines = lines.filter((line, index) => {
        if (index === 0) return true; // keep header
        const cols = line.split(",").map((c) => c.trim());
        return cols.some((c) => c !== "");
      });

      const headers = cleanedLines[0].split(",");
      const columnIndexesToFormat = [];

      headers.forEach((header, index) => {
        const trimmedHeader = header.trim().toLowerCase();
        if (targetColumns.map(c => c.toLowerCase()).includes(trimmedHeader)) {
          columnIndexesToFormat.push(index);
        }
      });
      // Function to normalize any date to dd/mm/yyyy
      const normalizeDate = (val) => {
        if (!val) return val;
        const clean = val.trim().replace(/-/g, "/");
        const match = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [_, d, m, y] = match;
          return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
        }
        return val;
      };

      const formattedLines = cleanedLines.map((line, lineIndex) => {
        if (lineIndex === 0) return line;
        const columns = line.split(",");
        columnIndexesToFormat.forEach((colIdx) => {
          if (columns[colIdx]) {
            columns[colIdx] = normalizeDate(columns[colIdx]);
          }
        });
        return columns.join(",");
      });

      const formattedContent = formattedLines.join("\n");
      const blob = new Blob([formattedContent], { type: "text/csv" });
      const updatedFile = new File([blob], file.name, { type: "text/csv" });
      resolve(updatedFile);
    };

    reader.onerror = (err) => reject(err);
    reader.readAsText(file);
  });
};

export const getGeoCodeComplianceValue = (params) => {
  return params.value === '1' || params.value === 'true' ? 'true' : 'false';
}